import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { db } from "./firebase";

const OZ_TO_GRAM = 31.103; // Standard troy ounce to gram conversion
const USD_TO_INR_FALLBACK = 83.3;
const CACHE_TTL_MS = 0; // Disabled cache - fetch fresh rates every time

// Metal Premium Defaults (can be overridden from Firebase)
const DEFAULT_PREMIUMS = {
  gold: 0,
  silver: 0,
  platinum: 0,
};

export const METALS = [
  { key: "gold", label: "Gold", symbol: "XAU" },
  { key: "silver", label: "Silver", symbol: "XAG" },
  { key: "platinum", label: "Platinum", symbol: "XPT" },
];

export const RATE_FIELDS = [
  { key: "rtgsSale", label: "RTGS Sale" },
  { key: "rtgsPurchase", label: "RTGS Purchase" },
  { key: "metalSale", label: "Metal Sale" },
  { key: "metalPurchase", label: "Metal Purchase" },
];

export const INDIA_STATES = [
  { stateName: "Andhra Pradesh", stateCode: "AP" },
  { stateName: "Arunachal Pradesh", stateCode: "AR" },
  { stateName: "Assam", stateCode: "AS" },
  { stateName: "Bihar", stateCode: "BR" },
  { stateName: "Chhattisgarh", stateCode: "CG" },
  { stateName: "Goa", stateCode: "GA" },
  { stateName: "Gujarat", stateCode: "GJ" },
  { stateName: "Haryana", stateCode: "HR" },
  { stateName: "Himachal Pradesh", stateCode: "HP" },
  { stateName: "Jharkhand", stateCode: "JH" },
  { stateName: "Karnataka", stateCode: "KA" },
  { stateName: "Kerala", stateCode: "KL" },
  { stateName: "Madhya Pradesh", stateCode: "MP" },
  { stateName: "Maharashtra", stateCode: "MH" },
  { stateName: "Manipur", stateCode: "MN" },
  { stateName: "Meghalaya", stateCode: "ML" },
  { stateName: "Mizoram", stateCode: "MZ" },
  { stateName: "Nagaland", stateCode: "NL" },
  { stateName: "Odisha", stateCode: "OD" },
  { stateName: "Punjab", stateCode: "PB" },
  { stateName: "Rajasthan", stateCode: "RJ" },
  { stateName: "Sikkim", stateCode: "SK" },
  { stateName: "Tamil Nadu", stateCode: "TN" },
  { stateName: "Telangana", stateCode: "TS" },
  { stateName: "Tripura", stateCode: "TR" },
  { stateName: "Uttar Pradesh", stateCode: "UP" },
  { stateName: "Uttarakhand", stateCode: "UK" },
  { stateName: "West Bengal", stateCode: "WB" },
  { stateName: "Andaman & Nicobar Islands", stateCode: "AN" },
  { stateName: "Chandigarh", stateCode: "CH" },
  { stateName: "Dadra & Nagar Haveli and Daman & Diu", stateCode: "DN" },
  { stateName: "Delhi", stateCode: "DL" },
  { stateName: "Jammu & Kashmir", stateCode: "JK" },
  { stateName: "Ladakh", stateCode: "LA" },
  { stateName: "Lakshadweep", stateCode: "LD" },
  { stateName: "Puducherry", stateCode: "PY" },
];

const STATE_NAME_TO_CODE = INDIA_STATES.reduce(
  (acc, state) => {
    acc[state.stateName.toLowerCase()] = state.stateCode;
    return acc;
  },
  { orissa: "OD", "new delhi": "DL", "jammu and kashmir": "JK" },
);

export const DEFAULT_RATES = {
  RTGS_SALE_RATE: 0,
  RTGS_PURCHASE_RATE: 0,
  METAL_SALE_RATE: 0,
  METAL_PURCHASE_RATE: 0,
};

let cachedBaseRates = null;
let cachedAt = 0;
let cachedPremiums = DEFAULT_PREMIUMS;

export function normalizeStateCode(state) {
  const raw = String(state || "").trim();
  if (!raw) return "";
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  return STATE_NAME_TO_CODE[raw.toLowerCase()] || raw.toUpperCase();
}

const makeRateSet = (perGram) => ({
  rtgsSale: Number(perGram.toFixed(2)),
  rtgsPurchase: Number(perGram.toFixed(2)),
  metalSale: Number(perGram.toFixed(2)),
  metalPurchase: Number(perGram.toFixed(2)),
});

/**
 * Fetch metal premiums from Firebase
 */
async function fetchMetalPremiums() {
  try {
    const snap = await getDoc(doc(db, "systemConfig", "metalPremiums"));
    if (snap.exists()) {
      const data = snap.data();
      const premiumsData = data.premiums || {}; // SuperAdmin stores as { premiums: {...} }
      cachedPremiums = {
        gold: Number(premiumsData.gold ?? DEFAULT_PREMIUMS.gold),
        silver: Number(premiumsData.silver ?? DEFAULT_PREMIUMS.silver),
        platinum: Number(premiumsData.platinum ?? DEFAULT_PREMIUMS.platinum),
      };
      return cachedPremiums;
    }
  } catch (err) {
    console.warn("[fetchMetalPremiums] Failed to fetch premiums:", err);
  }
  return cachedPremiums;
}

let cachedConstraints = null;
let cachedConstraintsAt = 0;
const CONSTRAINTS_CACHE_MS = 60 * 1000;

export async function fetchIndiaBaseRates() {
  const now = Date.now();
  if (cachedBaseRates && now - cachedAt < CACHE_TTL_MS) return cachedBaseRates;

  // Fetch metal premiums from Firebase
  const premiums = await fetchMetalPremiums();

  // Fetch spot prices in USD and USD/INR exchange rate
  const [goldRes, silverRes, platinumRes, fxRes] = await Promise.all([
    fetch("https://api.gold-api.com/price/XAU"),
    fetch("https://api.gold-api.com/price/XAG"),
    fetch("https://api.gold-api.com/price/XPT"),
    fetch("https://open.er-api.com/v6/latest/USD"),
  ]);

  const goldJson = goldRes.ok ? await goldRes.json() : null;
  const silverJson = silverRes.ok ? await silverRes.json() : null;
  const platinumJson = platinumRes.ok ? await platinumRes.json() : null;
  const fxJson = fxRes.ok ? await fxRes.json() : null;

  const usdToInr = fxJson?.rates?.INR || USD_TO_INR_FALLBACK;

  if (!usdToInr) {
    throw new Error("Could not fetch USD to INR exchange rate");
  }

  /**
   * Indian Base Rate Calculation
   * Formula: Indian Base Rate = (Spot USD × USD/INR ÷ 28.35) × (1 + Premium%)
   * Expanded: Indian Base Rate = (Spot USD × USD/INR ÷ 28.35) + (Spot USD × USD/INR ÷ 28.35) × (Premium%)
   *
   * Step 1: Convert spot price to INR per gram = (Spot USD × USD/INR ÷ 28.35)
   * Step 2: Calculate premium amount = base × (Premium% / 100)
   * Step 3: Indian Base Rate = base + premium amount
   */
  const pricePerGram = (usdPerOz, premiumPct) => {
    if (!usdPerOz) return null;
    const baseInr = (usdPerOz * usdToInr) / OZ_TO_GRAM;
    const premiumAmount = baseInr * (premiumPct / 100);
    return baseInr + premiumAmount;
  };

  const goldPerGram = pricePerGram(goldJson?.price, premiums.gold);
  const silverPerGram = pricePerGram(silverJson?.price, premiums.silver);
  const platinumPerGram = pricePerGram(platinumJson?.price, premiums.platinum);

  cachedBaseRates = {
    gold: goldPerGram ? makeRateSet(goldPerGram) : null,
    silver: silverPerGram ? makeRateSet(silverPerGram) : null,
    platinum: platinumPerGram ? makeRateSet(platinumPerGram) : null,
    raw: {
      gold: goldJson,
      silver: silverJson,
      platinum: platinumJson,
      goldPerGram,
      silverPerGram,
      platinumPerGram,
      usdToInr,
      premiums,
    },
    updatedAt: new Date().toISOString(),
  };
  cachedAt = now;

  console.log("[fetchIndiaBaseRates] Calculated base rates:", {
    goldSpotUsd: goldJson?.price,
    silverSpotUsd: silverJson?.price,
    platinumSpotUsd: platinumJson?.price,
    usdToInr,
    premiums,
    goldPerGram: goldPerGram?.toFixed(2),
    silverPerGram: silverPerGram?.toFixed(2),
    platinumPerGram: platinumPerGram?.toFixed(2),
  });

  return cachedBaseRates;
}

export async function fetchStateConstraint(state) {
  const stateCode = normalizeStateCode(state);
  if (!stateCode) return null;

  const snap = await getDoc(doc(db, "rateConstraints", stateCode));
  if (!snap.exists()) return { stateCode, active: true };
  const data = snap.data();
  return {
    stateCode,
    stateName: data.stateName || stateCode,
    active: data.active !== false,
    gold: data.gold || {},
    silver: data.silver || {},
    platinum: data.platinum || {},
  };
}

export async function fetchAllStateConstraints() {
  const now = Date.now();
  if (cachedConstraints && now - cachedConstraintsAt < CONSTRAINTS_CACHE_MS) {
    return cachedConstraints;
  }

  try {
    const snap = await getDocs(collection(db, "rateConstraints"));
    const constraints = {};
    snap.forEach((item) => {
      constraints[item.id.toUpperCase()] = item.data();
    });
    cachedConstraints = constraints;
    cachedConstraintsAt = now;
    return constraints;
  } catch (err) {
    console.warn("[fetchAllStateConstraints] Error fetching constraints:", err);
    return cachedConstraints || {};
  }
}

/**
 * Calculate State Rate from Indian Base Rate and State Constraint %
 * Formula: State Rate = Indian Base Rate + (Indian Base Rate × State Constraint%)
 * Alternative: State Rate = Indian Base Rate × (1 + State Constraint% / 100)
 */
export function calculateStateRate(baseRate, adjustmentPct) {
  const base = Number(baseRate) || 0;
  const adjustment = Number(adjustmentPct) || 0;

  if (!Number.isFinite(base) || base <= 0) return 0;

  // State Rate = Indian Base Rate × (1 + State Constraint% / 100)
  const finalRate = base * (1 + adjustment / 100);
  return Number(finalRate.toFixed(2));
}

export function calculateStateRates(baseRates, constraint, metalKey = "gold") {
  const base = baseRates?.[metalKey] || {};
  // Get adjustment percentages from constraint, defaulting to 0 if not configured
  const adjustment = {
    rtgsSale: Number(constraint?.[metalKey]?.rtgsSale) || 0,
    rtgsPurchase: Number(constraint?.[metalKey]?.rtgsPurchase) || 0,
    metalSale: Number(constraint?.[metalKey]?.metalSale) || 0,
    metalPurchase: Number(constraint?.[metalKey]?.metalPurchase) || 0,
  };

  // If constraint is inactive, use base rates directly (0% adjustment)
  if (constraint?.active === false) {
    return {
      RTGS_SALE_RATE: Number(base.rtgsSale) || 0,
      RTGS_PURCHASE_RATE: Number(base.rtgsPurchase) || 0,
      METAL_SALE_RATE: Number(base.metalSale) || 0,
      METAL_PURCHASE_RATE: Number(base.metalPurchase) || 0,
      INDIA_RTGS_SALE: Number(base.rtgsSale) || 0,
      INDIA_RTGS_PURCHASE: Number(base.rtgsPurchase) || 0,
      INDIA_METAL_SALE: Number(base.metalSale) || 0,
      INDIA_METAL_PURCHASE: Number(base.metalPurchase) || 0,
      STATE_CODE: constraint?.stateCode || "",
      STATE_NAME: constraint?.stateName || constraint?.stateCode || "",
      SEGMENT: metalKey,
      ADJUSTMENT: {
        rtgsSale: 0,
        rtgsPurchase: 0,
        metalSale: 0,
        metalPurchase: 0,
      },
    };
  }

  return {
    RTGS_SALE_RATE: calculateStateRate(base.rtgsSale, adjustment.rtgsSale),
    RTGS_PURCHASE_RATE: calculateStateRate(
      base.rtgsPurchase,
      adjustment.rtgsPurchase,
    ),
    METAL_SALE_RATE: calculateStateRate(base.metalSale, adjustment.metalSale),
    METAL_PURCHASE_RATE: calculateStateRate(
      base.metalPurchase,
      adjustment.metalPurchase,
    ),
    INDIA_RTGS_SALE: Number(base.rtgsSale) || 0,
    INDIA_RTGS_PURCHASE: Number(base.rtgsPurchase) || 0,
    INDIA_METAL_SALE: Number(base.metalSale) || 0,
    INDIA_METAL_PURCHASE: Number(base.metalPurchase) || 0,
    STATE_CODE: constraint?.stateCode || "",
    STATE_NAME: constraint?.stateName || constraint?.stateCode || "",
    SEGMENT: metalKey,
    ADJUSTMENT: adjustment,
  };
}

export async function fetchSellerStateRates(sellerState, metalKey = "gold") {
  try {
    const [baseRates, constraint] = await Promise.all([
      fetchIndiaBaseRates(),
      fetchStateConstraint(sellerState),
    ]);
    return calculateStateRates(baseRates, constraint, metalKey);
  } catch (error) {
    console.error(
      "[fetchSellerStateRates] Failed to fetch state rates:",
      error,
    );
    return {
      ...DEFAULT_RATES,
      STATE_CODE: normalizeStateCode(sellerState),
      SEGMENT: metalKey,
    };
  }
}

export async function fetchSellerRegisteredState(sellerId) {
  if (!sellerId) return "";
  try {
    const snap = await getDoc(doc(db, "sellerregistrations", sellerId));
    if (!snap.exists()) return "";
    const data = snap.data();
    return data.contactState || data.state || "";
  } catch (error) {
    console.warn(
      "[fetchSellerRegisteredState] Failed to fetch seller state:",
      error,
    );
    return "";
  }
}

export async function fetchAllStateRates() {
  const baseRates = await fetchIndiaBaseRates();
  const constraints = await fetchAllStateConstraints();

  return INDIA_STATES.map((state) => {
    const constraintData = constraints[state.stateCode] || {};
    const constraint = {
      stateCode: state.stateCode,
      stateName: state.stateName,
      active: constraintData.active !== false, // Only true if explicitly not false
      gold: constraintData.gold ? { ...constraintData.gold } : {},
      silver: constraintData.silver ? { ...constraintData.silver } : {},
      platinum: constraintData.platinum ? { ...constraintData.platinum } : {},
    };

    return {
      ...state,
      metals: METALS.reduce((acc, metal) => {
        acc[metal.key] = calculateStateRates(baseRates, constraint, metal.key);
        return acc;
      }, {}),
    };
  });
}

export function formatCurrencyINR(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
